import { LogOut, Menu } from "lucide-react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, useBrand } from "@/context/BrandContext";
import { AlertBell } from "@/components/AlertBell";

const NAV = [
  { to: "/", label: "FLEET", roles: ["admin", "dispatcher", "viewer"] },
  { to: "/trips", label: "TRIPS", roles: ["admin", "dispatcher"] },
  { to: "/vehicles", label: "VEHICLES", roles: ["admin"] },
  { to: "/drivers", label: "DRIVERS", roles: ["admin"] },
  { to: "/playback", label: "PLAYBACK", roles: ["admin", "dispatcher"] },
  { to: "/geofences", label: "GEOFENCES", roles: ["admin", "dispatcher"] },
  { to: "/admin", label: "CONTROL SUITE", roles: ["admin", "dispatcher"] },
  { to: "/users", label: "USERS", roles: ["admin"] },
  { to: "/branding", label: "BRANDING", roles: ["admin"] },
];

export const TopBar = ({ onMenu, children }) => {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const links = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <header className="shrink-0 border-b border-white/10">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {onMenu && (
            <button
              data-testid="mobile-menu-btn"
              onClick={onMenu}
              className="shrink-0 border border-white/10 p-1.5 text-[#A1A1AA] lg:hidden"
              aria-label="Open fleet roster"
            >
              <Menu size={16} />
            </button>
          )}
          <BrandMark brand={brand} size={18} />
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {children}
          <AlertBell />
          <div className="hidden text-right sm:block">
            <p className="text-[11px] leading-tight" data-testid="current-user">
              {user?.username}
            </p>
            <p className="overline">{user?.role === "viewer" ? "view only" : user?.role}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="border border-white/10 p-1.5 text-[#A1A1AA] transition-colors duration-150 hover:border-[#FF3B30] hover:text-[#FF3B30]"
            aria-label="Log out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>

      <nav
        className="doot-scroll flex items-center gap-1 overflow-x-auto border-t border-white/5 px-4 py-2 sm:px-6"
        data-testid="main-nav"
      >
        {links.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `shrink-0 whitespace-nowrap border px-2.5 py-1 text-[10px] tracking-[0.2em] transition-colors duration-150 ${
                isActive
                  ? "border-[#00F0FF] text-[#00F0FF]"
                  : "border-transparent text-[#A1A1AA] hover:text-white"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

export const PageShell = ({ title, subtitle, actions, children, testid }) => (
  <div className="flex h-screen flex-col overflow-hidden bg-[#0A0A0A]" data-testid={testid}>
    <TopBar />
    <div className="doot-scroll flex-1 overflow-y-auto px-4 py-8 sm:px-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
          <div>
            <p className="overline">{subtitle}</p>
            <h1 className="font-head text-3xl font-black tracking-tight sm:text-4xl">{title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </div>
        <div className="mt-8 pb-16">{children}</div>
      </div>
    </div>
  </div>
);

export const cellCls = "px-3 py-3 text-xs align-middle";
export const headCls = "px-3 py-2 text-left overline";
export const inputCls =
  "w-full border border-white/15 bg-[#121212] px-3 py-2 text-xs text-white outline-none focus:border-[#00F0FF]";
export const btnPrimary =
  "flex items-center justify-center gap-2 border border-[#00F0FF] bg-[#00F0FF] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#00F0FF] disabled:opacity-50";
export const btnGhost =
  "flex items-center gap-1.5 border border-white/15 px-3 py-2 text-[10px] tracking-[0.2em] text-white transition-colors duration-150 hover:border-[#00F0FF] hover:text-[#00F0FF]";
export const btnDanger =
  "flex items-center gap-1.5 border border-white/10 px-2 py-1.5 text-[10px] tracking-[0.2em] text-[#A1A1AA] transition-colors duration-150 hover:border-[#FF3B30] hover:text-[#FF3B30]";
